import React, { useState } from 'react';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

import caregiverService from '../../services/caregiverService';

/**
 * Confirmation modal for deactivating a caregiver-participant link (FECOA-1374).
 * Re-activation is intentionally not supported. The modal copy says so.
 *
 * Props:
 *   link: { link_id, caregiver: { id, ... }, relationship_type, ... } row from
 *         the Caregivers tab response. The caller passes either the row directly
 *         or any object with `link_id` + `caregiver` shape.
 *   caregiverId: optional override; falls back to link.caregiver.id.
 */
const DeactivateLinkConfirm = ({ link, caregiverId, onClose, onConfirmed }) => {
  const [submitting, setSubmitting] = useState(false);
  const cgId = caregiverId || link?.caregiver?.id;
  const linkId = link?.link_id || link?.id;
  const cgName =
    link?.caregiver?.full_name ||
    `${link?.caregiver?.first_name || ''} ${link?.caregiver?.last_name || ''}`.trim() ||
    link?.caregiver_id ||
    'this caregiver';

  const confirm = async () => {
    if (!cgId || !linkId) return;
    setSubmitting(true);
    try {
      await caregiverService.deactivateLink(cgId, linkId);
      toast.success('Caregiver link deactivated.');
      onConfirmed && onConfirmed();
    } catch (err) {
      console.error('deactivate failed', err);
      toast.error(err?.response?.data?.detail || 'Failed to deactivate link.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70">
      <div className="w-full max-w-md animate-slide-up rounded-xl bg-white p-6 shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Deactivate Caregiver Link</h2>
          <button
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
            className="btn btn-ghost btn-sm btn-icon -mr-2 -mt-1"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        <div className="mb-5 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="text-xs text-amber-800 dark:text-amber-200">
            <p className="font-semibold mb-1">This action cannot be reversed.</p>
            <p>
              Deactivating ends {cgName}&rsquo;s link to this participant for audit purposes.
              To reinstate this caregiver later, create a new link. The previous record stays
              archived for the audit trail.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={submitting}
            className="btn btn-danger"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Deactivate Link'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeactivateLinkConfirm;
